import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import { User } from '../../shared/models/user';
import {AuthenticationService, CompanyService, PermissionService} from "../../core";
import {NzDrawerService} from "ng-zorro-antd/drawer";
import {UserDrawerComponent} from "../../shared/components/user-drawer/user-drawer.component";
import {NzModalService} from "ng-zorro-antd/modal";
import {ConfirmModalComponent} from "../../shared/components/confirm-modal/confirm-modal.component";
import {Subject, take, takeUntil} from "rxjs";
import {OidcSecurityService} from "angular-auth-oidc-client";
import {NavigationService} from "../../core/services/navigation.service";
import {environment} from "../../../environments/environment";
import { ExportTrackingService } from '../../core';

@Component({
  selector: 'app-dashboard-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrls: ['./dashboard-layout.component.less']
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  mode: 'user' | 'company' = 'company';
  title = 'Inicio';
  description = 'Bienvenido a tu espacio de trabajo personal';
  currentUser: User | undefined;

  // Estados de menús desplegables
  isReportsExpanded = false;
  isManagementExpanded = false;

  // Export tracking
  showExportTracking = false;
  exportCount = 0;

  confirmLogoutModalRef: any;
  userDrawerRef: any;

  // Permisos para mostrar/ocultar elementos del menú
  canAccessDashboard = false;
  canAccessUsers = false;
  canAccessRoles = false;
  canAccessIntegrations = false;
  canReadFavorite = false;
  canReadReports = false;

  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private drawerService: NzDrawerService,
    private modalService: NzModalService,
    private oidcSecurityService: OidcSecurityService,
    private companyService: CompanyService,
    private permissionService: PermissionService,
    private navigationService: NavigationService,
    private exportTrackingService: ExportTrackingService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.authService.loggedUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.currentUser = user;
      if (user) {
        this.checkPermissions(); // Verificar permisos para mostrar/ocultar elementos del menú
      }
    });

    this.navigationService.navigaionData$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value) {
        setTimeout(() => {
          this.title = value.title;
          this.description = value.description;
        });
      }
    });

    // Suscribirse a las exportaciones para actualizar el contador
    this.exportTrackingService.exports
      .pipe(takeUntil(this.destroy$))
      .subscribe(exports => {
        this.exportCount = exports.length;
        this.cdr.detectChanges();
      });
  }

  /**
   * Verifica los permisos del usuario para mostrar/ocultar elementos del menú
   */
  private checkPermissions(): void {
    this.canAccessDashboard = this.permissionService.canAccessDashboard();
    this.canAccessUsers = this.permissionService.canAccessUsers();
    this.canAccessRoles = this.permissionService.canAccessRoles();
    this.canAccessIntegrations = this.permissionService.canAccessIntegrations();
    this.canReadFavorite = this.permissionService.canReadFavorite();
    this.canReadReports = this.permissionService.hasPermission('CanReadReports');
    this.cdr.detectChanges();
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  navigate(path: string): void {
    this.router.navigate([path]).then();
  }

  toggleReportsDropdown(): void {
    this.isReportsExpanded = !this.isReportsExpanded;
  }

  toggleManagementDropdown(): void {
    this.isManagementExpanded = !this.isManagementExpanded;
  }

  openUserDrawer(): void {
    this.drawerService.create({
      nzContent: UserDrawerComponent,
      nzFooter: '',
      nzWidth: 336,
      nzClosable: false,
      nzMask: true,
      nzMaskStyle: {
        'backdrop-filter': 'blur(4px)',
        '-webkit-backdrop-filter': 'blur(4px)' // Soporte para navegadores WebKit (Safari, Chrome antiguos)
      }
    })
  }

  openConfirmLogout(): void {
    this.confirmLogoutModalRef = this.modalService.create({
      nzContent: ConfirmModalComponent,
      nzFooter: null,
      nzCentered: true,
      nzMask: true,
      nzMaskStyle: {
        'backdrop-filter': 'blur(4px)',
        '-webkit-backdrop-filter': 'blur(4px)' // Soporte para navegadores WebKit (Safari, Chrome antiguos)
      }
    })

    this.confirmLogoutModalRef.getContentComponent().topIcon = {
      name: 'triangle-alert',
      color: '#088050',
      backgroundColor: '#C8F0E0',
    }
    this.confirmLogoutModalRef.getContentComponent().title = '¿Cerrar sesión ahora?';
    this.confirmLogoutModalRef.getContentComponent().content = 'Si cierras tu sesión, podrías perder cambios no guardados. Podrás volver a ingresar cuando lo desees.';
    this.confirmLogoutModalRef.getContentComponent().confirmText = 'Cerrar sesión';
    this.confirmLogoutModalRef.getContentComponent().type = 'confirm'
    // Manejo del confirm
    this.confirmLogoutModalRef.getContentComponent().confirm.pipe(take(1)).subscribe(() => {
      this.confirmLogoutModalRef.getContentComponent().loading = true;
      this.oidcSecurityService
        .logoff()
        .subscribe(() => {
          this.companyService.selectedCompany = undefined;
          this.confirmLogoutModalRef.getContentComponent().loading = false;
          this.confirmLogoutModalRef.close();
        });
    });
  }

  get isAdmin(): boolean {
    return !!(this.currentUser && this.currentUser.roles[0].isAdmin);
  }


  protected readonly environment = environment;

  isReportsActive() {
    return this.title === 'Reportes' || (this.currentUser?.reports || []).some(r => r.name === this.title);
  }

  toggleExportTracking(): void {
    this.showExportTracking = !this.showExportTracking;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
